import { useEffect, useState } from 'react';
import { get } from '../../services/GenesisApi';

type SubContract = {
  contractId: number;
  contractReference: string;
  isExpired: number;
  parentId: number;
  counterpartyId: number;
  typeId: number;
  powerAutomateMappingTagId: number;
  powerAutomateMappingTagName: string;
  startDate: string;
  endDate: string;
};

type ContractItem = {
  contractId: number;
  contractReference: string;
  isExpired: number;
  parentId: number;
  counterpartyId: number;
  typeId: number;
  powerAutomateMappingTagId: number;
  powerAutomateMappingTagName: string;
  startDate: string;
  endDate: string;
  subContracts?: SubContract[];
};

type SelectOption = { id: number; name: string };

// Helper function to extract subcontracts as SelectOptions
function extractSubContracts(subContracts: SubContract[]): SelectOption[] {
  return subContracts.map((subContract) => ({
    id: subContract.contractId,
    name: subContract.contractReference,
  }));
}

export default function useSwingContracts() {
  const [contracts, setContracts] = useState<SelectOption[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    get('/api/SwingContract')
      .then((resp) => {
        const data: ContractItem[] = resp?.data ?? [];
        if (!mounted) {
          return;
        }

        // Flatten contracts and subcontracts into a single array
        const allContracts: SelectOption[] = [];

        data.forEach((contract) => {
          // Add parent contract
          allContracts.push({
            id: contract.contractId,
            name: contract.contractReference,
          });

          // Add subcontracts if they exist
          if (contract.subContracts && contract.subContracts.length > 0) {
            allContracts.push(...extractSubContracts(contract.subContracts));
          }
        });

        setContracts(allContracts);
      })
      .catch(() => {
        if (!mounted) {
          return;
        }
        setContracts([]);
      })
      .finally(() => {
        if (!mounted) {
          return;
        }
        setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  return { contracts, loading };
}
